import { useId } from 'react'
import type { ReactNode } from 'react'

/**
 * Shared "field chrome" for the labeled form primitives — Input, Select,
 * Textarea, Upload (UID-229 review #190/#191). Before this, each primitive
 * hand-copied the same id/`aria-describedby` wiring, required-mark markup, and
 * help/error rendering (and their `.x-label`/`.x-help-text`/`.x-error-text`
 * CSS), which had already started to drift. This is the single source.
 *
 * Per the review's own guidance it is NOT a monolithic wrapper: it's a small
 * hook (`useFieldIds`) plus a few presentational pieces (`FieldLabel`,
 * `FieldMessages`) that captioned fields compose and Upload — whose "label" is
 * a button face, not a `<label>` — reuses only the parts that fit
 * (`useFieldIds` + `FieldMessages`).
 */

export interface FieldIds {
  readonly id: string
  readonly errorId: string
  readonly helpId: string
  /** The single id `aria-describedby` should reference: `errorId` when an error
   * is shown, else `helpId`, else `undefined` — matching `FieldMessages`'
   * error-XOR-help render so the referenced element always actually exists (no
   * dangling idref). Fields that render help AND error together (e.g. the auth
   * FormField, hint-above + error-below) build their own describedBy instead. */
  readonly describedBy: string | undefined
}

export function useFieldIds(
  providedId: string | undefined,
  opts: { readonly error?: unknown; readonly helpText?: unknown },
): FieldIds {
  const generatedId = useId()
  const id = providedId ?? generatedId
  const errorId = `${id}-error`
  const helpId = `${id}-help`
  const describedBy = opts.error ? errorId : opts.helpText ? helpId : undefined
  return { id, errorId, helpId, describedBy }
}

/** `*` + a screen-reader "(required)" — never the asterisk glyph alone
 * (accessibility-wcag). Appended inside a field's `<label>`. */
export function RequiredMark() {
  return (
    <>
      <span aria-hidden="true" className="field-required-mark">
        {' '}
        *
      </span>
      <span className="sr-only"> (required)</span>
    </>
  )
}

export interface FieldLabelProps {
  readonly htmlFor: string
  /** Visually hide the label (still exposed to assistive tech) for dense
   * inline controls — never drop it entirely (that's the a11y review comment
   * these primitives exist to prevent). */
  readonly hideLabel?: boolean | undefined
  readonly required?: boolean | undefined
  readonly children: ReactNode
}

export function FieldLabel({
  htmlFor,
  hideLabel = false,
  required = false,
  children,
}: FieldLabelProps) {
  return (
    <label htmlFor={htmlFor} className={hideLabel ? 'sr-only' : 'field-label'}>
      {children}
      {required && <RequiredMark />}
    </label>
  )
}

export interface FieldMessagesProps {
  /** The field's ids (from `useFieldIds`) — one object instead of loose
   * `errorId`/`helpId` props, so a caller can't pass a mismatched pair. */
  readonly ids: Pick<FieldIds, 'errorId' | 'helpId'>
  readonly error?: string | undefined
  readonly helpText?: string | undefined
}

/** The mutually-exclusive help/error line under a field — error takes
 * precedence over help (so it matches `useFieldIds`' describedBy). */
export function FieldMessages({ ids, error, helpText }: FieldMessagesProps) {
  if (error) {
    return (
      <p id={ids.errorId} className="field-error-text">
        {error}
      </p>
    )
  }
  if (helpText) {
    return (
      <p id={ids.helpId} className="field-help-text">
        {helpText}
      </p>
    )
  }
  return null
}
